import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  HANDOFF_MAX_COVER_MS,
  HANDOFF_REASSURE_AFTER_MS,
  HANDOFF_RELOAD_AFTER_MS,
  clearHandoff,
  getConversationIdFromPath,
  getDestinationReadiness,
  getHandoffCaption,
  getHandoffProgress,
  readPendingHandoff,
} from "../helpers/deepLinkHandoff";

// Grace after the destination reports ready, so its own first paint (e.g.
// ChatLayout's 300ms settle) happens under the cover, not in front of the user.
const SETTLE_GRACE_MS = 400;
// Routes with no readiness signal are uncovered once the path has been stable
// this long (restarts on every navigation, so redirect chains stay covered).
const GENERIC_SETTLE_MS = 700;
// The bar fills and the cover fades over this long once the destination is
// ready, so the reveal reads as "done" rather than a sudden swap.
const EXIT_MS = 300;
// How often the bar and reassurance line are recomputed while visible.
const TICK_MS = 100;

/**
 * One full-screen loader for the whole `?token=` deep-link journey, mounted
 * once in App. See `helpers/deepLinkHandoff` for the flow and for
 * `getDestinationReadiness`, the only place routes are special-cased. Routes
 * need no changes: readiness is read from state they already publish.
 *
 * Visually it matches the static boot splash in index.html (centred, pulsing
 * logo), so splash → overlay → destination reads as one continuous step.
 */
export default function DeepLinkLoader({ isSigningIn }) {
  const location = useLocation();
  // Read once, before the destination paints.
  const [handoff] = useState(() =>
    readPendingHandoff(window.location.pathname)
  );
  const [isHandoffDone, setIsHandoffDone] = useState(!handoff);
  const chat = useSelector((state) => state.chat);

  // Latches once the chat page flags our conversation as loading; its later
  // clearing is the "resolved, one way or another" signal.
  const sawUrlLoading = useRef(false);
  const conversationId = getConversationIdFromPath(handoff?.pathname);
  if (
    conversationId &&
    chat?.urlConversationLoading?.isLoading &&
    chat.urlConversationLoading.conversationId === conversationId
  ) {
    sawUrlLoading.current = true;
  }

  const readiness = isHandoffDone
    ? true
    : getDestinationReadiness(
        handoff,
        chat,
        location.pathname,
        sawUrlLoading.current
      );

  useEffect(() => {
    if (isHandoffDone || readiness === false) return undefined;
    const timer = setTimeout(
      () => setIsHandoffDone(true),
      readiness === null ? GENERIC_SETTLE_MS : SETTLE_GRACE_MS
    );
    return () => clearTimeout(timer);
  }, [isHandoffDone, readiness, location.pathname]);

  // Never cover the destination longer than the cap, whatever happens.
  useEffect(() => {
    if (isHandoffDone) return undefined;
    const timer = setTimeout(
      () => setIsHandoffDone(true),
      HANDOFF_MAX_COVER_MS
    );
    return () => clearTimeout(timer);
  }, [isHandoffDone]);

  useEffect(() => {
    if (isHandoffDone) clearHandoff();
  }, [isHandoffDone]);

  // Done → keep the cover for EXIT_MS (bar at 100%, fading) → unmount.
  const [isGone, setIsGone] = useState(!handoff);
  useEffect(() => {
    if (!isHandoffDone || isGone) return undefined;
    const timer = setTimeout(() => setIsGone(true), EXIT_MS);
    return () => clearTimeout(timer);
  }, [isHandoffDone, isGone]);

  const isVisible = isSigningIn || !isGone;

  // Re-render on a tick so the bar keeps creeping forward while we wait.
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    if (!isVisible) return undefined;
    const timer = setInterval(() => setNow(performance.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [isVisible]);

  if (!isVisible) return null;

  const isLeaving = !isSigningIn && isHandoffDone;
  const stage = isSigningIn ? "signingIn" : "opening";
  const progress = isLeaving ? 1 : getHandoffProgress(stage, now);
  const caption = isSigningIn
    ? "Signing you in…"
    : getHandoffCaption(handoff?.pathname);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy={!isLeaving}
      data-testid="deep-link-loader"
      className="fixed inset-0 flex flex-col items-center justify-center bg-white"
      style={{
        // Above route content, modals and the chat widget.
        zIndex: 2147483000,
        opacity: isLeaving ? 0 : 1,
        transition: `opacity ${EXIT_MS}ms ease-out`,
      }}
    >
      <svg
        className="animate-pulse"
        fill="none"
        height="40"
        width="40"
        viewBox="0 0 102 102"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M60.1689 67.1544c-6.9654-2.9925-12.9058-7.0037-22.4116-15.8665-18.114-16.796-26.9068-36.0688-19.4639-42.94508 4.6224-4.34863 18.8271-4.19583 36.7883 8.91368"
          stroke="#000"
          strokeLinecap="round"
          strokeWidth="6.36695"
          strokeLinejoin="round"
        />
        <path
          d="M67.1111 31.1001C80.7746 46.5718 88.918 63.5715 82.7293 69.8048c-2.4322 2.4322-6.4116 2.9925-11.3268 1.9674"
          stroke="#000"
          strokeLinecap="round"
          strokeWidth="6.36695"
        />
        <path
          d="M41.9653 42.9448c13.4979-18.7635 29.6127-27.1232 38.8384-22.469 9.6778 4.858 6.367 25.5569-7.3156 46.1604-13.6826 20.6035-32.7389 33.4008-42.4167 28.543-8.0032-4.0239-7.2902-19.7375.9232-36.368"
          stroke="#000"
          strokeLinecap="round"
          strokeWidth="6.36695"
        />
      </svg>
      <p className="mt-6 text-sm font-semibold text-gray-700">{caption}</p>
      <p className="mt-1 text-xs text-gray-400">
        Step {isSigningIn ? 1 : 2} of 2
      </p>
      <div
        className="mt-3 h-1 w-48 overflow-hidden rounded-full bg-gray-100"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${progress * 100}%`,
            backgroundColor: "#5F2EEA",
            transition: `width ${isLeaving ? EXIT_MS : TICK_MS}ms linear`,
          }}
        />
      </div>
      <p
        className="mt-3 text-xs text-gray-500"
        style={{
          opacity: now > HANDOFF_REASSURE_AFTER_MS && !isLeaving ? 1 : 0,
          transition: "opacity 400ms ease-out",
        }}
      >
        This usually takes a few seconds
      </p>
      {now > HANDOFF_RELOAD_AFTER_MS && !isLeaving && (
        <p
          className="absolute left-0 right-0 mt-8 text-center text-sm font-semibold text-gray-700"
          style={{ top: "calc(50% + 5rem)" }}
        >
          Taking too long to respond?{" "}
          <button
            type="button"
            className="font-semibold"
            style={{ color: "#5F2EEA" }}
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </p>
      )}
    </div>
  );
}
